import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { nip19 } from 'nostr-tools'
import { useAppView } from '@/hooks/useAppView'
import { useAuth } from '@/hooks/useAuth'
import { getProfileGraphQL, isActivityPubHandle, type ATExploreProfile } from '@/lib/atExplore'
import { getAPFollowedBy } from '@/lib/mastodon'
import { fetchNostrProfile } from '@/services/nostrService'
export interface ViewProfileData {
    profile: ATExploreProfile | undefined
    isLoading: boolean
    error: Error | null
    isAP: boolean
    // Bridge status (now from GraphQL query)
    isBridged: boolean
    bridgedHandle: string | undefined
    bridgedDid: string | undefined
    nostrPubkey: string | undefined
    bridgeLoading: boolean
    // Stats (now from GraphQL query)
    stats:
        | {
              followersCount: number
              followsCount: number
              postsCount: number
          }
        | undefined
    statsLoading: boolean
    /** Truthy if the viewed user follows the authenticated user. For AT Protocol this is the AT URI of the follow record; for AP profiles this is the string "true". */
    followedBy: string | undefined
    // Refresh
    refetchProfile: () => Promise<unknown>
    isFetchingProfile: boolean
}

export function useViewProfile(identifier: string | undefined): ViewProfileData {
    const { publicAgent, appView, sessionAgent } = useAppView()
    const { session, mastodonSession, nostrSession } = useAuth()
    const isAP = identifier ? isActivityPubHandle(identifier) : false
    const isNostr = !!identifier?.startsWith('npub')

    // Decode npub to hex pubkey for Nostr profile lookups
    const nostrPubkeyHex = useMemo(() => {
        if (!isNostr || !identifier) return null
        try {
            const decoded = nip19.decode(identifier)
            return decoded.type === 'npub' ? (decoded.data as string) : null
        } catch {
            return null
        }
    }, [isNostr, identifier])

    // Shortened npub for display (e.g. "npub1abc...xyz1")
    const nostrShortHandle = useMemo(() => {
        if (!isNostr || !identifier) return undefined
        return `${identifier.slice(0, 8)}...${identifier.slice(-4)}`
    }, [isNostr, identifier])

    // Single GraphQL query for profile + stats + bridge status.
    // Include session?.did so the query re-runs when auth loads, refreshing any
    // session-dependent data (e.g. followedBy) once the user is authenticated.
    // Use the authenticated, AppView-proxy-aware sessionAgent when logged in: the
    // getProfile call must be authenticated for the AppView to populate
    // viewer.followedBy (required for the "Follows you" badge). sessionAgent proxies
    // to the selected AppView (e.g. Blacksky) via service auth, so this still
    // respects the AppView toggle. Falls back to the unauthenticated publicAgent when
    // logged out, where no viewer-relative state is available anyway.
    // Nostr profiles skip this query — at-explore has no Nostr resolver.
    const profileAgent = sessionAgent ?? publicAgent
    const profileQuery = useQuery({
        queryKey: ['viewProfile', 'graphql', identifier, appView, session?.did],
        queryFn: () => getProfileGraphQL(identifier!, { publicAgent: profileAgent }),
        enabled: !!identifier && !isNostr,
        staleTime: 2 * 60 * 1000, // 2 minutes — profile data changes frequently
        retry: 1,
    })

    // Nostr profile query — fetches kind-0 metadata directly from relays
    const nostrProfileQuery = useQuery({
        queryKey: ['viewProfile', 'nostr', identifier],
        queryFn: () => fetchNostrProfile(nostrPubkeyHex!, nostrSession?.relays),
        enabled: isNostr && !!nostrPubkeyHex,
        staleTime: 5 * 60 * 1000,
        retry: 1,
    })

    // Map Nostr kind-0 data to the ATExploreProfile shape used by ViewProfile
    const nostrProfile = useMemo((): ATExploreProfile | undefined => {
        if (!isNostr || !nostrPubkeyHex) return undefined
        const data = nostrProfileQuery.data
        return {
            did: nostrPubkeyHex,
            handle: nostrShortHandle ?? nostrPubkeyHex,
            displayName: data?.display_name ?? data?.name,
            description: data?.about,
            avatar: data?.picture,
            banner: data?.banner,
            website: data?.website,
        }
    }, [isNostr, nostrPubkeyHex, nostrShortHandle, nostrProfileQuery.data])

    // For AP profiles: check whether the viewed account follows the authenticated Mastodon user.
    // Runs in parallel with the main profile query and does not block profile display.
    const apFollowedByQuery = useQuery({
        queryKey: [
            'viewProfile',
            'apFollowedBy',
            identifier,
            mastodonSession?.instance_url,
            mastodonSession?.account?.username,
        ],
        queryFn: async () => {
            const followed = await getAPFollowedBy(mastodonSession!, identifier!)
            return followed
        },
        enabled: isAP && !!identifier && !!mastodonSession,
        staleTime: 2 * 60 * 1000,
        retry: false,
    })

    const queryData = profileQuery.data

    // Extract bridged handle from bridge status
    const bridgedHandle = isAP
        ? queryData?.bridgeStatus?.blueskyHandle
        : queryData?.bridgeStatus?.activityPubHandle

    // Memoize derived profile to keep a stable reference across re-renders
    const profile = useMemo(
        () =>
            queryData
                ? {
                      did: queryData.did,
                      handle: queryData.handle,
                      displayName: queryData.displayName,
                      description: queryData.description,
                      avatar: queryData.avatar,
                      banner: queryData.banner,
                      followersCount: queryData.followersCount,
                      followsCount: queryData.followsCount,
                      postsCount: queryData.postsCount,
                      isVerified: queryData.isVerified,
                      pronouns: queryData.pronouns,
                      bridgyOriginalUrl: queryData.bridgyOriginalUrl,
                      extendedBio: queryData.extendedBio,
                      linkedAccounts: queryData.linkedAccounts?.map(a => ({
                          ...a,
                          id: a.rkey ?? `${a.name}-${a.link}`,
                      })),
                      website: queryData.website,
                      labels: queryData.labels,
                      collections: queryData.collections,
                  }
                : undefined,
        [queryData]
    )

    const stats = useMemo(
        () =>
            queryData?.followersCount != null
                ? {
                      followersCount: queryData.followersCount,
                      followsCount: queryData.followsCount ?? 0,
                      postsCount: queryData.postsCount ?? 0,
                  }
                : undefined,
        [queryData]
    )

    if (isNostr) {
        return {
            profile: nostrProfile,
            isLoading: nostrProfileQuery.isLoading,
            error: nostrProfileQuery.error,
            isAP: false,
            isBridged: false,
            bridgedHandle: undefined,
            bridgedDid: undefined,
            nostrPubkey: undefined,
            bridgeLoading: false,
            stats: undefined,
            statsLoading: false,
            followedBy: undefined,
            refetchProfile: () => nostrProfileQuery.refetch(),
            isFetchingProfile: nostrProfileQuery.isFetching,
        }
    }

    return {
        profile,
        isLoading: profileQuery.isLoading,
        error: profileQuery.error,
        isAP,
        // Bridge status (from GraphQL response)
        isBridged: queryData?.bridgeStatus?.isBridged ?? false,
        bridgedHandle,
        bridgedDid: isAP ? queryData?.bridgeStatus?.blueskyDid : undefined,
        nostrPubkey: queryData?.bridgeStatus?.nostrPubkey,
        bridgeLoading: false, // Now part of main query
        // Stats (from GraphQL response)
        stats,
        statsLoading: false, // Now part of main query
        followedBy: isAP
            ? apFollowedByQuery.data === true
                ? 'true'
                : undefined
            : queryData?.followedBy,
        // Refresh
        refetchProfile: () => profileQuery.refetch(),
        isFetchingProfile: profileQuery.isFetching,
    }
}
